import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PlusIcon, SearchIcon, FilterIcon, AlertTriangleIcon, ClipboardListIcon, BanknoteIcon, WalletIcon } from 'lucide-react';
import { Input, Option, Select } from '@material-tailwind/react';
import MaintenanceBoard from './components/MaintenanceBoard';
import WorkOrderFormModal from './components/WorkOrderFormModal';
import QuickViewModal from './components/QuickViewModal';
import TabBar from './components/TabBar';
import ReportsView from './ReportsView';
import { URLS } from '../../router-x/routes';
import { usePermissions } from '../../custom-hooks/use-permissions';
import { useGetMaintenanceSummary, useGetWorkOrders } from '../../custom-hooks/use-maintenance';
import { useGetUsers } from '../../custom-hooks/use-users';
import { useGetFloors } from '../../custom-hooks/use-floors';
import useDebounce from '../../custom-hooks/useDebounce';
import { MAINTENANCE_CATEGORIES_ENUM, MAINTENANCE_PRIORITIES_ENUM, STATIC_USER_ROLES } from '../../utils/enums';
import { formatNumberWithCommasPakistani } from '../../utils/functions';
import { CATEGORY_META, PRIORITY_META } from './utils';

const ALL_TABS = [
    { key: 'board', label: 'Board' },
    { key: 'reports', label: 'Reports' },
];

const StatCard = ({ icon: Icon, label, value, tone = 'default' }) => (
    <div className="flex items-center gap-3 rounded-2xl border border-inv-cream-200 bg-white p-4 shadow-sm">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tone === 'alert' ? 'bg-red-50 text-red-600' : 'bg-inv-brand-light text-inv-brand'}`}>
            <Icon size={18} />
        </div>
        <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#8C8175]">{label}</p>
            <p className="text-lg font-extrabold text-inv-ink">{value}</p>
        </div>
    </div>
);

const MaintenanceModule = () => {
    const { can } = usePermissions();
    const navigate = useNavigate();
    const canViewCosts = can('FX32', 'view_costs');
    const tabs = canViewCosts ? ALL_TABS : ALL_TABS.filter((t) => t.key !== 'reports');
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedTab = searchParams.get('tab');
    const statusParam = searchParams.get('status'); // executive drill-through: open|overdue
    const activeTab = tabs.some((t) => t.key === requestedTab) ? requestedTab : 'board';
    const setActiveTab = (tab) => setSearchParams(tab === 'board' ? {} : { tab });
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [quickViewWorkOrder, setQuickViewWorkOrder] = useState(null);
    const [showFilters, setShowFilters] = useState(false);

    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 400);
    const [category, setCategory] = useState('');
    const [priority, setPriority] = useState('');
    const [assignee, setAssignee] = useState('');
    const [floor, setFloor] = useState('');

    const canCreate = can('FX32', 'create');
    const canMove = can('FX32', 'update_status') || can('FX32', 'edit');

    const activeFilterCount = [search, category, priority, assignee, floor].filter(Boolean).length;

    const filters = useMemo(() => ({
        _search: debouncedSearch || undefined,
        category: category || undefined,
        priority: priority || undefined,
        assignee: assignee || undefined,
        floor: floor || undefined,
    }), [debouncedSearch, category, priority, assignee, floor]);

    const { data: workOrders = [], isLoading } = useGetWorkOrders(filters);
    const { data: summary = {} } = useGetMaintenanceSummary();

    // Executive drill-through: ?status=overdue filters to past-due open WOs
    const filteredWorkOrders = useMemo(() => {
        if (statusParam !== 'overdue') return workOrders;
        const now = Date.now();
        return workOrders.filter((wo) => wo.dueDate && new Date(wo.dueDate).getTime() < now
            && wo.status !== 'completed' && wo.status !== 'cancelled');
    }, [workOrders, statusParam]);
    const { data: staffData } = useGetUsers({
        role: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.EMPLOYEE, STATIC_USER_ROLES.STAFF],
        _page: 1,
        _limit: 1000,
    });
    const staff = staffData?.users || [];
    const { data: floors = [] } = useGetFloors();

    return (
        // Fixed to the viewport (matches the sidebar shell's own h-screen content area)
        // so this page never scrolls itself — only the board area below does, via its
        // own overflow-x-auto. Without this, the board's calc'd height and the outer
        // page's natural scroll fought each other, leaving stray gaps and a second,
        // unstyled native scrollbar.
        <div className="flex h-screen flex-col bg-inv-cream animate-invFadeIn">
            <div className="shrink-0 px-4 pt-6 md:px-8 md:pt-8">
                <div className="mb-6 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
                    <div>
                        <h1 className="text-2xl font-extrabold tracking-tight text-inv-ink md:text-[26px]">
                            Maintenance &amp; Repairs
                        </h1>
                        <p className="mt-1 text-sm text-[#8C8175]">
                            Manage facility work orders, repairs, decor, and enhancements.
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        {tabs.length > 1 && (
                            <TabBar tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
                        )}

                        {activeTab === 'board' && (
                            <>
                                <button
                                    onClick={() => setShowFilters((v) => !v)}
                                    className={`relative flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors ${
                                        showFilters ? 'border-inv-brand bg-inv-brand-light text-inv-brand' : 'border-inv-cream-200 bg-white text-inv-ink hover:bg-inv-cream-100'
                                    }`}
                                >
                                    <FilterIcon size={16} />
                                    Filters
                                    {activeFilterCount > 0 && (
                                        <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-inv-brand px-1 text-[10px] font-bold text-white">
                                            {activeFilterCount}
                                        </span>
                                    )}
                                </button>

                                {canCreate && (
                                    <button
                                        onClick={() => setIsCreateModalOpen(true)}
                                        className="flex items-center gap-2 rounded-xl bg-inv-brand px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-inv-brand-dark hover:shadow-md active:scale-95"
                                    >
                                        <PlusIcon size={18} />
                                        Quick Create
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
                    <StatCard icon={ClipboardListIcon} label="Open Items" value={summary.openCount ?? '—'} />
                    <StatCard icon={AlertTriangleIcon} label="Overdue" value={summary.overdueCount ?? '—'} tone={summary.overdueCount ? 'alert' : 'default'} />
                    {canViewCosts && (
                        <>
                            <StatCard icon={BanknoteIcon} label="Est. Spend (Month)" value={`${formatNumberWithCommasPakistani(summary.totalEstimateThisMonth || 0)} PKR`} />
                            <StatCard icon={WalletIcon} label="Actual Spend (Month)" value={`${formatNumberWithCommasPakistani(summary.totalActualSpendThisMonth || 0)} PKR`} />
                        </>
                    )}
                </div>

                {activeTab === 'board' && showFilters && (
                    <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-inv-cream-200 bg-white p-3 shadow-sm animate-invFadeIn md:flex-row md:flex-wrap md:items-center">
                        <div className="min-w-[200px] flex-1">
                            <Input
                                icon={<SearchIcon size={16} />}
                                label="Search title"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="w-full shrink-0 sm:w-56">
                            <Select label="Category" value={category} onChange={(v) => setCategory(v || '')} selected={() => CATEGORY_META[category]?.label || 'All'}>
                                <Option value="">All</Option>
                                {MAINTENANCE_CATEGORIES_ENUM.map((c) => <Option key={c} value={c}>{CATEGORY_META[c]?.label || c}</Option>)}
                            </Select>
                        </div>
                        <div className="w-full shrink-0 sm:w-56">
                            <Select label="Priority" value={priority} onChange={(v) => setPriority(v || '')} selected={() => PRIORITY_META[priority]?.label || 'All'}>
                                <Option value="">All</Option>
                                {MAINTENANCE_PRIORITIES_ENUM.map((p) => <Option key={p} value={p}>{PRIORITY_META[p]?.label || p}</Option>)}
                            </Select>
                        </div>
                        <div className="w-full shrink-0 sm:w-56">
                            <Select label="Assignee" value={assignee} onChange={(v) => setAssignee(v || '')} menuProps={{ className: 'max-h-64 overflow-y-auto' }} selected={() => staff.find((u) => u._id === assignee)?.name || 'All'}>
                                <Option value="">All</Option>
                                {staff.map((u) => <Option key={u._id} value={u._id}>{u.name || u.email}</Option>)}
                            </Select>
                        </div>
                        <div className="w-full shrink-0 sm:w-56">
                            <Select label="Floor" value={floor} onChange={(v) => setFloor(v || '')} selected={() => (floors.find((f) => f._id === floor) ? `Floor ${floors.find((f) => f._id === floor).number}` : 'All')}>
                                <Option value="">All</Option>
                                {floors.map((f) => <Option key={f._id} value={f._id}>{`Floor ${f.number}${f.floorCode ? ` (${f.floorCode})` : ''}`}</Option>)}
                            </Select>
                        </div>
                    </div>
                )}
            </div>

            {/* min-h-0 is what lets a flex child actually shrink and scroll internally
                instead of growing to fit its content and pushing the page taller. The
                board handles its own horizontal scroll; Reports needs vertical instead. */}
            <div className={`min-h-0 flex-1 px-4 pb-6 md:px-8 md:pb-8 ${activeTab === 'reports' ? 'overflow-y-auto custom-scrollbar' : ''}`}>
                {activeTab === 'reports' ? (
                    <ReportsView />
                ) : isLoading ? (
                    <div className="flex h-full items-center justify-center text-sm text-[#8C8175]">Loading work orders…</div>
                ) : (
                    <MaintenanceBoard
                        workOrders={filteredWorkOrders}
                        canViewCosts={canViewCosts}
                        canMove={canMove}
                        onCardClick={(workOrder) => navigate(`${URLS.ADMIN.MAINTENANCE}/${workOrder._id}`)}
                        onQuickView={setQuickViewWorkOrder}
                    />
                )}
            </div>

            <WorkOrderFormModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                workOrder={null}
            />

            <QuickViewModal
                isOpen={!!quickViewWorkOrder}
                onClose={() => setQuickViewWorkOrder(null)}
                workOrder={quickViewWorkOrder}
                canViewCosts={canViewCosts}
            />
        </div>
    );
};

export default MaintenanceModule;
